
export class IndexedDBFactory<T extends { id: IDBValidKey }> {
    // static inject = () => [SupportGroupDataProvider];
    // constructor(private dataProvider: SupportGroupDataProvider) {}

    // either creates or returns an existing DB
    get = (dbName: string, storeName: string, version: number = 1) => {
        return new IndexedDBManager<T>(dbName, storeName, version);
    };
};

export class IndexedDBManager<T extends { id: IDBValidKey }> {
    private dbPromise: Promise<IDBDatabase>;
    private dbName: string;
    private storeName: string;
    private version: number;

    constructor(dbName: string, storeName: string, version: number = 1) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.version = version;
        this.dbPromise = this.initializeDB();
    };

    private initializeDB = async (): Promise<IDBDatabase> => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = (event) => {
                reject(`IndexedDB error: ${(event.target as IDBRequest).error}`);
            };

            request.onsuccess = (event) => {
                resolve((event.target as IDBRequest).result);
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
        });
    };

    async getCapacityEstimate() {
        const estimate = await navigator.storage.estimate();
        console.log(estimate);
        return estimate;
    };

    private getTransaction = async () => {
        let tx: IDBTransaction;
        const db = await this.dbPromise;

        try {
            tx = db.transaction(this.storeName, 'readwrite');
        } catch(err: any) {
            if (err.name === 'InvalidStateError') {
                await this.initializeDB();
                tx = db.transaction(this.storeName, 'readwrite');
            } else {
                throw err;
            }
        }

        return tx;
    };

    async add(data: T): Promise<IDBValidKey> {
        return new Promise(async (resolve, reject) => {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);
            const request = store.add(data);

            request.onsuccess = (event) => resolve((event.target as IDBRequest).result as IDBValidKey);
            request.onerror = (event) => reject(`Error adding data: ${(event.target as IDBRequest).error}`);
        });
    };

    async get(id: IDBValidKey): Promise<T | undefined> {
        return new Promise(async (resolve, reject) => {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);
            const request = store.get(id);

            request.onsuccess = (event) => resolve((event.target as IDBRequest).result as T);
            request.onerror = (event) => reject(`Error getting data: ${(event.target as IDBRequest).error}`);
        });
    };

    async update(data: T): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);
            const request = store.get(data.id);

            request.onsuccess = (event) => {
                const existingData = (event.target as IDBRequest).result as T;
                if (existingData) {
                    // Merge existing data with updated data
                    const mergedData = { ...existingData, ...data };
                    const putRequest = store.put(mergedData);

                    putRequest.onsuccess = () => resolve();
                    putRequest.onerror = (errEvent) => reject(`Error updating data: ${(errEvent.target as IDBRequest).error}`);
                } else {
                    reject(new Error(`Record with key ${data.id} not found`));
                }
            };

            request.onerror = (event) => reject(`Error getting data: ${(event.target as IDBRequest).error}`);
        });
    };

    async delete(id: IDBValidKey): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);
            const request = store.delete(id);

            request.onsuccess = (event) => {
                console.log(`deleted recording with id ${id}`);
                resolve();
            };

            request.onerror = (event) => reject(`Error deleting data: ${(event.target as IDBRequest).error}`);
        });
    };

    async getAll(): Promise<T[]> {
        return new Promise(async (resolve, reject) => {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);
            const request = store.getAll();

            request.onsuccess = (event) => resolve((event.target as IDBRequest).result as T[]);
            request.onerror = (event) => reject(`Error getting all data: ${(event.target as IDBRequest).error}`);
        });
    };
};
